import { router, useLocalSearchParams } from 'expo-router';
import { useAtom } from 'jotai';
import React from 'react';
import type { Edge } from 'react-native-safe-area-context';
import { isWeb, SafeAreaView, ScrollView, YStack } from 'smoothie';

import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { manualBillAtom } from '../../state/BillsAtoms';
import {
    BillContext,
    BillEditTypeParam,
    type IBill,
    type ManualBill,
    type PartialBill,
} from '../../types/Bill';
import BillEditAccount from '../common/BillEditAccount';
import BillEditCategory from '../common/BillEditCategory';
import BillEditFrequency from '../common/BillEditFrequency';
import BillEditLastPaid from '../common/BillEditLastPaid';
import BillDetailsEditHeader from '../edit/BillDetailsEditHeader';

interface ManualBillEditContainerProps {
    context?: BillContext;
}

const ManualBillEditContainer = ({ context }: ManualBillEditContainerProps) => {
    LogRocketFacade.tagPage('ManualBillEdit');
    const { type } = useLocalSearchParams<{ type: BillEditTypeParam }>();

    const [manualBill, setManualBill] = useAtom(manualBillAtom);

    const onChange = (bill: IBill | PartialBill | ManualBill) => {
        setManualBill(bill as ManualBill);
        // delaying the back action briefly so there's visual feedback that a value was selected
        setTimeout(() => {
            router.back();
        }, 100);
    };

    const getEditComponent = () => {
        let componentTitle = '';
        let component: React.JSX.Element | null = null;

        switch (type) {
            case BillEditTypeParam.ACCOUNT:
                component = (
                    <BillEditAccount bill={manualBill} onChange={onChange} />
                );
                componentTitle = 'Account';
                break;
            case BillEditTypeParam.FREQUENCY:
                component = (
                    <BillEditFrequency bill={manualBill} onChange={onChange} />
                );
                componentTitle = 'Frequency';
                break;
            case BillEditTypeParam.CATEGORY:
                component = (
                    <BillEditCategory bill={manualBill} onChange={onChange} />
                );
                componentTitle = 'Category';
                break;
            case BillEditTypeParam.LAST_PAYMENT_DATE:
                component = (
                    <BillEditLastPaid bill={manualBill} onChange={onChange} />
                );
                componentTitle = 'Last paid';
                break;
            default:
                return null;
        }

        return (
            <SafeAreaView
                edges={[
                    'right',
                    'bottom',
                    'left',
                    ...(context === BillContext.REVIEW ? ['top' as Edge] : []),
                ]}
            >
                <YStack
                    flex={1}
                    maxWidth={700}
                    width="100%"
                    alignSelf="center"
                    padding={20}
                    paddingTop={
                        isWeb ? 24 : context === BillContext.REVIEW ? 0 : 20
                    }
                    gap={12}
                >
                    <BillDetailsEditHeader title={componentTitle} />
                    <ScrollView>
                        <YStack>{component}</YStack>
                    </ScrollView>
                </YStack>
            </SafeAreaView>
        );
    };

    return <>{getEditComponent()}</>;
};

export default ManualBillEditContainer;
